import { useMemo } from 'react';

import { LOCALES } from '~/libs/constant/locale';

import { useLanguage } from './useLanguage';

/**
 * 언어, 국가, 시간과 관련된 Localization 함수들을 제공합니다.
 */
export const useLocale = () => {
  const { languageKey } = useLanguage();

  const formatLangauge = (language: string) => {
    if (!language) return 'en';
    const splittedLanguage = language.replace('_', '-').split('-');

    const formattedLang = splittedLanguage[0].toLocaleLowerCase();
    const formattedRegion = splittedLanguage?.[1]?.toLocaleUpperCase();

    if (!formattedRegion) return formattedLang;
    return `${formattedLang}-${formattedRegion}`;
  };

  const convertLanguageToLocale = (language: string) => {
    const formattedLanguage = formatLangauge(language);
    return (
      LOCALES.find((e) => {
        if (e === formattedLanguage) return true;
        const [lang] = e.split('-');
        if (lang === formattedLanguage) return true;
        return false;
      }) || 'en-US'
    );
  };

  const locale = useMemo(() => {
    return convertLanguageToLocale(languageKey);
  }, [languageKey]);

  const region = useMemo(() => {
    const { region } = new Intl.Locale(locale);
    return region;
  }, [locale]);

  const timezone = useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, [locale]);

  return {
    /**
     * 언어 문자열을 올바른 형태로 변환합니다.
     *
     * @param {string} language
     * @see https://ko.wikipedia.org/wiki/ISO_639-1_%EC%BD%94%EB%93%9C_%EB%AA%A9%EB%A1%9D
     */
    formatLangauge,
    /**
     * 해당 언어를 사용하는 국가를 '추정'하여 언어 태그를 반환합니다.
     *
     * @param {string} language
     * @see https://ko.wikipedia.org/wiki/IETF_%EC%96%B8%EC%96%B4_%ED%83%9C%EA%B7%B8
     */
    convertLanguageToLocale,
    /**
     * 현재 언어 태그를 반환합니다.
     *
     * @see https://ko.wikipedia.org/wiki/IETF_%EC%96%B8%EC%96%B4_%ED%83%9C%EA%B7%B8
     */
    locale,
    /**
     * 현재 Region string을 반환합니다.
     *
     * @see https://ko.wikipedia.org/wiki/ISO_3166-1
     */
    region,
    /**
     * 브라우저에서 감지한 타임존을 반환합니다.
     *
     * @see https://day.js.org/docs/en/timezone/timezone
     */
    timezone,
  };
};
